import { type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';

const IMAGE_UPLOAD_PLUGIN_KEY = new PluginKey('imageUploadHandler');

export const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

interface ImageUploadPluginOptions {
  uploadFile: (file: File) => Promise<string>;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
  onUploadError?: (error: Error) => void;
}

export const validateImageFile = (
  file: File,
  onError?: (error: Error) => void,
): boolean => {
  if (file.size > MAX_IMAGE_SIZE) {
    onError?.(new Error('Image must be smaller than 10MB'));
    return false;
  }
  return true;
};

// Ensures images are never nested inside lists, columns, etc. by finding document-level position
const findDocumentLevelPosition = (
  view: EditorView,
  dropPos: number,
): number => {
  const $pos = view.state.doc.resolve(dropPos);
  let insertPos = dropPos;

  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'doc') {
      if (depth === 0 && $pos.depth > 0) {
        const topLevelNode = $pos.node(1);
        const topLevelPos = $pos.before(1);
        insertPos = topLevelPos + topLevelNode.nodeSize;
      }
      break;
    }
  }

  return insertPos;
};

const findImageNodeByUploadId = (
  doc: ProseMirrorNode,
  uploadId: string,
): number | null => {
  let nodePos: number | null = null;
  doc.descendants((node, pos) => {
    if (node.type.name === 'image' && node.attrs.uploadId === uploadId) {
      nodePos = pos;
      return false; // Stop iterating
    }
    return true; // Continue iterating
  });
  return nodePos;
};

// Insert-first pattern: insert placeholder immediately, upload in background, then update with URL
const handleImageUpload = async (
  view: EditorView,
  file: File,
  options: ImageUploadPluginOptions,
  position?: number,
): Promise<void> => {
  const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
  const pos = position ?? view.state.selection.from;

  const tr = view.state.tr.insert(
    pos,
    view.state.schema.nodes.image.create({
      src: null,
      alt: file.name,
      isUploading: true,
      uploadId,
    }),
  );
  view.dispatch(tr);

  options.onUploadStart?.();

  try {
    const imageUri = await options.uploadFile(file);

    const currentState = view.state;
    const nodePos = findImageNodeByUploadId(currentState.doc, uploadId);

    if (nodePos !== null) {
      const node = currentState.doc.nodeAt(nodePos);
      if (!node) return;

      const updateTr = currentState.tr.setNodeMarkup(nodePos, undefined, {
        ...node.attrs,
        src: imageUri,
        alt: file.name,
        isUploading: false,
        uploadId: null,
      });
      view.dispatch(updateTr);
      options.onUploadComplete?.();
    } else {
      options.onUploadError?.(new Error('Upload cancelled'));
    }
  } catch (error) {
    options.onUploadError?.(error as Error);

    const currentState = view.state;
    const nodePos = findImageNodeByUploadId(currentState.doc, uploadId);

    if (nodePos !== null) {
      const node = currentState.doc.nodeAt(nodePos);
      if (!node) return;

      const deleteTr = currentState.tr.delete(nodePos, nodePos + node.nodeSize);
      view.dispatch(deleteTr);
    }
  }
};

export const createImageUploadPlugin = (options: ImageUploadPluginOptions) => {
  return new Plugin({
    key: IMAGE_UPLOAD_PLUGIN_KEY,

    props: {
      // Handle clipboard paste events
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find(item => item.type.startsWith('image/'));

        // No image in clipboard - let other handlers process it
        if (!imageItem) return false;

        const file = imageItem.getAsFile();
        if (!file) return false;

        // We have an image - prevent browser from opening it regardless of what we do next
        event.preventDefault();

        // Don't add to editor if not editable
        if (!view.editable) return true;

        // Don't add to editor if validation fails (error already shown via onUploadError)
        if (!validateImageFile(file, options.onUploadError)) {
          return true;
        }

        // Add to editor
        handleImageUpload(view, file, options);
        return true;
      },

      // Handle drop events
      handleDrop: (view, event, _slice, moved) => {
        // If this is an internal drag-drop (moving existing content), let TipTap handle it
        if (moved) return false;

        const files = Array.from(event.dataTransfer?.files || []);
        const imageFile = files.find(file => file.type.startsWith('image/'));

        if (!imageFile) return false;

        event.preventDefault();

        if (!view.editable) return true;
        if (!validateImageFile(imageFile, options.onUploadError)) {
          return true;
        }

        const coordinates = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });

        if (!coordinates) {
          return true;
        }

        const insertPos = findDocumentLevelPosition(view, coordinates.pos);
        handleImageUpload(view, imageFile, options, insertPos);
        return true;
      },
    },
  });
};
